import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { hydrateDutyRoster, type ThemeId } from "@northstar/dispatch-core";
import { loadSeenAcars, playSelcal, saveSeenAcars, unreadAcarsIds } from "./acarsInbox";
import { clockCrew, loadDutyClock, saveDutyClock } from "./dutyClock";
import { exitNativeApp } from "./nativeShell";
import { buildPilotBoard } from "./pilotBoard";
import {
  draftNeedsResume,
  emptyLogDraft,
  loadLogDrafts,
  saveLogDraft,
  type LogDraft,
} from "./logDraft";
import { CompanionShell } from "./shell/CompanionShell";
import type { EditTab } from "./shell/editTabs";
import type { FlightTab } from "./shell/tabs";
import { writeCompanionDuty } from "./syncSession";
import { useCompanion } from "./useCompanion";
import { AuthGate } from "./views/AuthGate";
import { FlightDetail } from "./views/FlightDetail";
import { PilotMore } from "./views/PilotMore";
import { TodayHome } from "./views/TodayHome";
import type { PilotScreen } from "./shell/PilotBar";
import { isFlightTab } from "./shell/tabs";

function syncLabel(status: string, roleLabel = ""): string {
  const base = status === "ok" ? "SYNC OK" : status === "error" ? "SYNC ERR" : status === "checking" ? "SYNC …" : "SYNC —";
  return roleLabel ? `${base} · ${roleLabel}` : base;
}

function deskRoleLabel(devices: { role?: string }[] | undefined): string {
  const roles = new Set((devices ?? []).map((row) => row.role).filter(Boolean));
  if (roles.has("main") && roles.has("secondary")) return "Main + Secondary";
  if (roles.has("secondary")) return "Secondary";
  if (roles.has("main")) return "Main";
  return "";
}

export function App() {
  const shotMode = import.meta.env.VITE_PILOT_SHOT === "1";
  const [shot, setShot] = useState<ReactNode>(null);
  useEffect(() => {
    if (!shotMode || typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const screen = params.get("shot");
    if (!screen) return;
    void import("./views/PilotShot").then((mod) => {
      setShot(
        <mod.PilotShot
          screen={screen}
          update={params.get("update")}
          theme={params.get("theme") === "dark" ? "dark" : "light"}
        />,
      );
    });
  }, [shotMode]);
  if (shot) return shot;
  return <LiveApp />;
}

function LiveApp() {
  const api = useCompanion();
  const storage = typeof localStorage === "undefined" ? null : localStorage;
  const [editTab] = useState<EditTab>("checklist");
  const [menuOpen, setMenuOpen] = useState(false);
  const [flightId, setFlightId] = useState<string | null>(null);
  const [flightTab, setFlightTab] = useState<FlightTab>("brief");
  const [screen, setScreen] = useState<PilotScreen>("home");
  const [aboutTick, setAboutTick] = useState(0);
  const [day, setDay] = useState("");
  const [drafts, setDrafts] = useState<LogDraft[]>(() => loadLogDrafts(storage));
  const [resume, setResume] = useState<LogDraft | null>(null);
  const [clockNote, setClockNote] = useState("");
  const [dutyRoster, setDutyRoster] = useState(() => loadDutyClock(storage));
  const flightRef = useRef(flightId);
  flightRef.current = flightId;
  const menuRef = useRef(menuOpen);
  menuRef.current = menuOpen;
  const board = useMemo(
    () => buildPilotBoard(api.snapshot?.payload ?? null, api.crewId, new Date(), dutyRoster),
    [api.snapshot, api.crewId, dutyRoster],
  );
  const [seenAcars, setSeenAcars] = useState(() => loadSeenAcars(storage));
  const playedAcars = useRef(new Set(seenAcars));
  const unreadIds = unreadAcarsIds(board.acarsUplinkIds, seenAcars);
  const unreadKey = unreadIds.join("|");
  useEffect(() => {
    const fresh = unreadKey.split("|").filter((id) => id && !playedAcars.current.has(id));
    if (!fresh.length) return;
    for (const id of fresh) playedAcars.current.add(id);
    playSelcal();
  }, [unreadKey]);

  function markAcarsRead() {
    const next = [...new Set([...seenAcars, ...board.acarsUplinkIds])].slice(-200);
    saveSeenAcars(storage, next);
    setSeenAcars(next);
  }

  function replyAcars(packet: string) {
    if (!board.tripId || board.acarsHoppie) return { ok: false as const, error: "Reply from the cockpit on this tail." };
    return api.writeAcars(board.tripId, packet);
  }

  useEffect(() => {
    const pending = loadLogDrafts(storage).find(draftNeedsResume) ?? null;
    setResume(pending);
  }, [api.account, storage]);

  function onTheme(next: ThemeId) {
    api.setChrome({ theme: next });
  }

  function openFlight(tab: FlightTab, id = board.tripId) {
    setScreen(tab);
    setFlightTab(tab);
    setFlightId(id ?? null);
    setMenuOpen(false);
    setResume(null);
  }

  function selectScreen(next: PilotScreen) {
    setMenuOpen(false);
    if (next === "home" || next === "more") {
      setScreen(next);
      setFlightId(null);
      return;
    }
    openFlight(next);
  }

  function onSystemBack() {
    if (menuRef.current) {
      setMenuOpen(false);
      return;
    }
    if (screen !== "home") {
      setScreen("home");
      setFlightId(null);
      return;
    }
    void exitNativeApp();
  }

  function onDraft(next: LogDraft) {
    setDrafts(saveLogDraft(next, storage));
  }

  async function onClock(action: "in" | "out") {
    const crewId = api.view.crewId;
    if (!crewId) {
      setClockNote("No crew on this board, so the clock stays unset.");
      return;
    }
    const next = clockCrew(dutyRoster ?? hydrateDutyRoster(api.snapshot?.payload?.duty ?? undefined), {
      id: crewId,
      name: api.view.crewName || crewId,
    }, action);
    saveDutyClock(next, storage);
    setDutyRoster(next);
    if (!board.canDispatchWrite || !api.account) {
      setClockNote(action === "in" ? "Clocked in on this phone." : "Clocked out on this phone.");
      return;
    }
    const posted = await writeCompanionDuty(api.settings, api.account, { crewId, duty: next });
    setClockNote(posted.ok ? "Duty clock posted to the board." : posted.error);
    if (posted.ok) void api.pull();
  }

  const draft = flightId
    ? drafts.find((row) => row.tripId === flightId) ??
      emptyLogDraft({ tripId: flightId, callsign: board.callsign, route: board.route })
    : null;

  if (!api.account) {
    return (
      <CompanionShell
        tab="trip"
        onTab={() => undefined}
        editTab={editTab}
        onEditTab={() => undefined}
        companyName="Pilot logon"
        companyLogo=""
        syncLabel={syncLabel(api.status, deskRoleLabel(api.snapshot?.payload.connectedDevices))}
        theme={api.chrome.theme}
        onTheme={onTheme}
        sheetOpen={false}
        onSheet={() => undefined}
        onSystemBack={() => void exitNativeApp()}
        locked
      >
        <AuthGate api={api} />
      </CompanionShell>
    );
  }

  return (
    <CompanionShell
      tab="trip"
      onTab={() => undefined}
      editTab={editTab}
      onEditTab={() => undefined}
      companyName={board.companyName.trim() || "No company"}
      companyLogo={board.companyLogo}
      syncLabel={syncLabel(api.status, deskRoleLabel(api.snapshot?.payload.connectedDevices))}
      theme={api.chrome.theme}
      onTheme={onTheme}
      sheetOpen={false}
      onSheet={() => undefined}
      onSignOut={() => void api.signOut()}
      onSystemBack={onSystemBack}
      onRefresh={() => void api.pull()}
      refreshing={api.busy}
      flightOpen={isFlightTab(screen)}
      flightTab={flightTab}
      onFlightTab={(next) => selectScreen(next)}
      screen={screen}
      onScreen={selectScreen}
      aboutTick={aboutTick}
      menuOpen={menuOpen}
      onMenu={setMenuOpen}
      menuUser={board.crewName}
      menuBase={board.homeIcao}
      drafts={drafts.map((row) => ({ tripId: row.tripId, label: `${row.callsign || "Log"} ${row.route}`.trim() }))}
      onOpenDraft={(tripId) => openFlight("log", tripId)}
      syncDown={api.status === "error"}
    >
      {resume ? (
        <section className="panel" data-testid="resume-draft" role="status">
          <p className="kicker">Unsaved log</p>
          <h2>
            {resume.callsign || "Log"} {resume.route}
          </h2>
          <p className="muted">
            {board.crash && board.tripId === resume.tripId
              ? "This flight is marked crashed. The log draft is still on this phone."
              : "A log draft is still on this phone."}
          </p>
          <div className="row">
            <button type="button" className="btn" onClick={() => openFlight("log", resume.tripId)}>
              Resume
            </button>
            <button type="button" className="btn ghost" onClick={() => setResume(null)}>
              Later
            </button>
          </div>
        </section>
      ) : null}
      {screen === "more" ? (
        <PilotMore
          api={api}
          refreshing={api.busy}
          onRefresh={() => void api.pull()}
          onAbout={() => setAboutTick((tick) => tick + 1)}
          drafts={drafts.map((row) => ({ tripId: row.tripId, label: `${row.callsign || "Log"} ${row.route}`.trim() }))}
          onOpenDraft={(tripId) => openFlight("log", tripId)}
          companyName={board.companyName.trim() || "No company"}
          companyLogo={board.companyLogo}
          menuUser={board.crewName}
          menuBase={board.homeIcao}
        />
      ) : isFlightTab(screen) ? (
        <FlightDetail
          board={board}
          tab={flightTab}
          draft={draft}
          onDraft={onDraft}
          onClock={(action) => void onClock(action)}
          clockNote={clockNote}
          unread={unreadIds.length}
          onMarkRead={markAcarsRead}
          onReply={board.acarsHoppie ? undefined : replyAcars}
        />
      ) : (
        <TodayHome
          board={board}
          day={day}
          onDay={setDay}
          onBrief={() => openFlight("brief")}
          onLog={() => openFlight("log")}
          unread={unreadIds.length}
          onMarkRead={markAcarsRead}
          onReply={board.acarsHoppie ? undefined : replyAcars}
        />
      )}
    </CompanionShell>
  );
}
